import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Phone, Mail, MessageCircle, ArrowUpRight, Loader2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBrandAndContact } from "@/hooks/useBrandAndContact";
import { SERVICES } from "@/data/services";
import PageShell from "@/components/PageShell";
import SectionTitle from "@/components/SectionTitle";
import DatePickerInput from "@/components/DatePickerInput";
import { useSEO } from "@/hooks/useSEO";
import { track } from "@/hooks/usePageViewTracker";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t, lang } = useLanguage();
  const CONTACT = useBrandAndContact();
  const location = useLocation();
  useSEO({
    title: t("contact_page.title"),
    description: t("contact_page.sub"),
    lang,
  });

  const defaultService = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("service");
    return SERVICES.some((x) => x.slug === s) ? s : "";
  }, [location.search]);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: defaultService,
    project_details: "",
    budget: "",
    preferred_date: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.service_type || !form.project_details) {
      toast.error(t("contact_page.required"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/contact`, {
        ...form,
        language: lang,
      });
      toast.success(t("contact_page.form_success"));
      track("order_submit", { meta: { service_type: form.service_type } });
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        project_details: "",
        budget: "",
        preferred_date: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t("contact_page.form_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell dataTestId="contact-page">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(197,160,89,0.3)",
            color: "#F2EBE5",
          },
        }}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16">
        <SectionTitle
          overline={t("contact_page.overline")}
          title={t("contact_page.title")}
          sub={t("contact_page.sub")}
          testId="contact-title"
        />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-32">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Direct contact */}
          <aside className="lg:col-span-5">
            <p className="overline kw-accent-line mb-6">
              {t("contact_page.direct_title")}
            </p>

            <div className="space-y-6">
              <motion.a
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                href={CONTACT.phoneHref}
                data-testid="contact-phone-link"
                className="group block border border-[#F2EBE5]/10 p-6 md:p-8 hover:border-[#C5A059]/50 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <Phone className="w-5 h-5 text-[#C5A059] mt-1" strokeWidth={1.25} />
                  <div className="flex-1">
                    <p className="kw-label mb-2">
                      {t("contact_page.phone_label")}
                    </p>
                    <p className="font-serif-display text-3xl md:text-4xl text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                      {CONTACT.phone}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                      {t("contact_page.call_cta")}
                      <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
                    </span>
                  </div>
                </div>
              </motion.a>

              <motion.a
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="group block border border-[#F2EBE5]/10 p-6 md:p-8 hover:border-[#C5A059]/50 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <MessageCircle
                    className="w-5 h-5 text-[#C5A059] mt-1"
                    strokeWidth={1.25}
                  />
                  <div className="flex-1">
                    <p className="kw-label mb-2">WhatsApp</p>
                    <p className="font-serif-display text-2xl md:text-3xl text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                      {CONTACT.phone}
                    </p>
                  </div>
                </div>
              </motion.a>

              <motion.a
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                href={CONTACT.emailHref}
                data-testid="contact-email-link"
                className="group block border border-[#F2EBE5]/10 p-6 md:p-8 hover:border-[#C5A059]/50 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <Mail className="w-5 h-5 text-[#C5A059] mt-1" strokeWidth={1.25} />
                  <div className="flex-1">
                    <p className="kw-label mb-2">
                      {t("contact_page.email_label")}
                    </p>
                    <p className="font-serif-display text-2xl md:text-3xl text-[#F2EBE5] group-hover:text-[#C5A059] transition-colors">
                      {CONTACT.email}
                    </p>
                  </div>
                </div>
              </motion.a>

              <div className="pt-4">
                <p className="text-xs uppercase tracking-[0.28em] text-[#A19E9C]">
                  {CONTACT.city}
                </p>
              </div>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <p className="overline kw-accent-line mb-6">
              {t("contact_page.form_title")}
            </p>

            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="space-y-10"
            >
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="kw-label block mb-3" htmlFor="name">
                    {t("contact_page.form_name")} *
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    data-testid="form-input-name"
                    className="kw-input"
                    placeholder="Kiwami..."
                  />
                </div>
                <div>
                  <label className="kw-label block mb-3" htmlFor="email">
                    {t("contact_page.form_email")} *
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    data-testid="form-input-email"
                    className="kw-input"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="kw-label block mb-3" htmlFor="phone">
                    {t("contact_page.form_phone")}
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    data-testid="form-input-phone"
                    className="kw-input"
                    placeholder="+212 ..."
                  />
                </div>
                <div>
                  <label className="kw-label block mb-3" htmlFor="service_type">
                    {t("contact_page.form_service")} *
                  </label>
                  <select
                    id="service_type"
                    name="service_type"
                    required
                    value={form.service_type}
                    onChange={handleChange}
                    data-testid="form-input-service"
                    className="kw-input"
                    style={{ colorScheme: "dark" }}
                  >
                    <option value="" className="bg-[#050505] text-[#F2EBE5]">
                      {t("contact_page.form_service_placeholder")}
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s.slug} value={s.slug} className="bg-[#050505] text-[#F2EBE5]">
                        {t(`services.${s.key}.label`)}
                      </option>
                    ))}
                    <option value="other" className="bg-[#050505] text-[#F2EBE5]">
                      {lang === "fr" ? "Autre" : "Other"}
                    </option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="kw-label block mb-3" htmlFor="budget">
                    {t("contact_page.form_budget")}
                  </label>
                  <input
                    id="budget"
                    name="budget"
                    type="text"
                    value={form.budget}
                    onChange={handleChange}
                    data-testid="form-input-budget"
                    className="kw-input"
                    placeholder="10 000 - 50 000 MAD"
                  />
                </div>
                <div>
                  <label className="kw-label block mb-3" htmlFor="preferred_date">
                    {t("contact_page.form_date")}
                  </label>
                  <DatePickerInput
                    value={form.preferred_date}
                    onChange={(v) =>
                      setForm((prev) => ({ ...prev, preferred_date: v }))
                    }
                    testId="form-input-date"
                    minDate={new Date()}
                  />
                </div>
              </div>

              <div>
                <label className="kw-label block mb-3" htmlFor="project_details">
                  {t("contact_page.form_details")} *
                </label>
                <textarea
                  id="project_details"
                  name="project_details"
                  required
                  rows={5}
                  value={form.project_details}
                  onChange={handleChange}
                  data-testid="form-input-details"
                  className="kw-input resize-none"
                  placeholder={t("contact_page.form_details_placeholder")}
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  data-testid="form-submit-btn"
                  disabled={submitting}
                  className="kw-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      {t("contact_page.form_submitting")}
                    </>
                  ) : (
                    <>
                      {t("contact_page.form_submit")}
                      <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Contact;
